import React from 'react';
import {
  IonApp,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonSpinner,
  setupIonicReact,
  useIonRouter,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Route, Redirect, useParams, useLocation, useHistory } from 'react-router-dom';
import {
  homeOutline,
  gridOutline,
  checkboxOutline,
} from 'ionicons/icons';

// Auth
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import DashboardEditor from './pages/DashboardEditor';
import TasksPage from './pages/TasksPage';
import AdminSettings from './pages/AdminSettings';

// Nested pages
import FamilyMemberProfile from './pages/FamilyMemberProfile';
import QuoteManager from './pages/QuoteManager';
import ScreenControl from './pages/ScreenControl';
import AnnouncementsPage from './pages/AnnouncementsPage';
import ListPage from './pages/ListPage';
import QuickAccessPage from './pages/QuickAccessPage';
import KioskDashboardPage from './pages/KioskDashboardPage';
import GoogleAuthCallback from './pages/GoogleAuthCallback';

// Components
import DashboardDisplay from './components/DashboardDisplay';

// Initialize Ionic
setupIonicReact({
  mode: 'ios', // Use iOS design for consistency across platforms
});

// ============================================
// DASHBOARD WRAPPER PAGES
// ============================================

// Dashboard list wrapper with navigation
const DashboardListPage: React.FC<{ householdId: string }> = ({ householdId }) => {
  const history = useHistory();

  return (
    <DashboardEditor
      householdId={householdId}
      onSelectDashboard={(dashboardId, editMode) => {
        const path = `/dashboards/view/${dashboardId}${editMode ? '?edit=true' : ''}`;
        console.log('[DashboardListPage] Navigating to:', path);
        history.push(path);
      }}
    />
  );
};

// Dashboard view wrapper for displaying/editing a dashboard
const DashboardViewPage: React.FC<{ householdId: string }> = ({ householdId }) => {
  const history = useHistory();
  const { dashboardId } = useParams<{ dashboardId: string }>();
  const location = useLocation();

  console.log('[DashboardViewPage] Rendering with dashboardId:', dashboardId, 'location:', location.pathname);

  // Parse edit mode from query params - re-runs when location changes
  const editMode = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('edit') === 'true';
  }, [location.search]);

  const [localEditMode, setLocalEditMode] = React.useState(editMode);

  // Sync local edit mode with URL
  React.useEffect(() => {
    setLocalEditMode(editMode);
  }, [editMode]);

  if (!dashboardId) {
    console.log('[DashboardViewPage] No dashboardId - returning null');
    return null;
  }

  // DashboardDisplay uses PageShell with disableScrollWrapper (IonicLayout.md escape hatch)
  return (
    <DashboardDisplay
      dashboardId={dashboardId}
      householdId={householdId}
      editMode={localEditMode}
      onEditModeChange={setLocalEditMode}
      onSettingsClick={() => history.push('/dashboards')}
    />
  );
};

// ============================================
// AUTHENTICATED APP SHELL
// ============================================

const AuthenticatedApp: React.FC = () => {
  const { currentHousehold } = useAuth();
  const householdId = currentHousehold?.id || '';
  const ionRouter = useIonRouter();

  // Navigate to tab - use 'root' direction for proper tab switching
  // 'root' replaces the page stack instead of pushing, avoiding the dual-page render issue
  const navigateToTab = (path: string) => (e: CustomEvent | React.MouseEvent) => {
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
    // Use 'root' for tab navigation to replace rather than stack pages
    ionRouter.push(path, 'root');
  };

  // If no household, show a message (this shouldn't happen with proper setup)
  if (!householdId) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: 24,
        textAlign: 'center',
      }}>
        <p style={{ fontSize: 18, marginBottom: 16 }}>No household found</p>
        <p style={{ color: 'var(--ion-color-medium)' }}>
          There was an issue setting up your household. Please try signing out and back in.
        </p>
      </div>
    );
  }

  return (
    <IonTabs>
      <IonRouterOutlet>
        {/* ═══════════════════════════════════════════
            HOME TAB
            ═══════════════════════════════════════════ */}
        <Route exact path="/home">
          <HomePage />
        </Route>

        {/* ═══════════════════════════════════════════
            DASHBOARDS TAB
            ═══════════════════════════════════════════ */}
        <Route exact path="/dashboards">
          <DashboardListPage householdId={householdId} />
        </Route>
        <Route
          exact
          path="/dashboards/view/:dashboardId"
          render={({ match }) => (
            <DashboardViewPage
              key={match.params.dashboardId}
              householdId={householdId}
            />
          )}
        />
        <Route exact path="/dashboards/screens">
          <ScreenControl householdId={householdId} />
        </Route>

        {/* ═══════════════════════════════════════════
            TASKS TAB
            ═══════════════════════════════════════════ */}
        <Route exact path="/tasks">
          <TasksPage />
        </Route>
        <Route path="/lists/:listId">
          <ListPage />
        </Route>

        {/* ═══════════════════════════════════════════
            SETTINGS TAB
            ═══════════════════════════════════════════ */}
        <Route exact path="/settings">
          <AdminSettings householdId={householdId} />
        </Route>
        <Route exact path="/settings/family/:memberId">
          <FamilyMemberProfile householdId={householdId} />
        </Route>
        <Route exact path="/settings/quotes">
          <QuoteManager householdId={householdId} />
        </Route>
        <Route exact path="/settings/announcements">
          <AnnouncementsPage />
        </Route>

        {/* ═══════════════════════════════════════════
            AUTH CALLBACKS
            ═══════════════════════════════════════════ */}
        <Route exact path="/auth/google/callback">
          <GoogleAuthCallback />
        </Route>

        {/* ═══════════════════════════════════════════
            DEFAULT REDIRECT
            ═══════════════════════════════════════════ */}
        <Route exact path="/">
          <Redirect to="/home" />
        </Route>
      </IonRouterOutlet>

      {/* ═══════════════════════════════════════════
          TAB BAR
          Settings removed - accessible via header gear icon on each page
          ═══════════════════════════════════════════ */}
      <IonTabBar slot="bottom">
        <IonTabButton tab="home" href="/home" onClick={navigateToTab('/home')}>
          <IonIcon icon={homeOutline} />
          <IonLabel>Home</IonLabel>
        </IonTabButton>

        <IonTabButton tab="dashboards" href="/dashboards" onClick={navigateToTab('/dashboards')}>
          <IonIcon icon={gridOutline} />
          <IonLabel>Dashboards</IonLabel>
        </IonTabButton>

        <IonTabButton tab="tasks" href="/tasks" onClick={navigateToTab('/tasks')}>
          <IonIcon icon={checkboxOutline} />
          <IonLabel>Tasks</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
};

// ============================================
// APP ROOT (handles auth state)
// ============================================

const AppRoot: React.FC = () => {
  const { user, loading } = useAuth();

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'var(--ion-background-color)',
      }}>
        <IonSpinner name="crescent" />
      </div>
    );
  }

  // Not authenticated - show login, quick access, or kiosk mode
  if (!user) {
    return (
      <IonReactRouter>
        <IonRouterOutlet>
          {/* Quick Access code entry */}
          <Route exact path="/access">
            <QuickAccessPage />
          </Route>
          {/* Legacy route - redirect to new path */}
          <Route exact path="/quick-access">
            <Redirect to="/access" />
          </Route>
          {/* Kiosk mode - display dashboard without authentication */}
          <Route exact path="/kiosk/:dashboardId">
            <KioskDashboardPage />
          </Route>
          {/* Default to login */}
          <Route>
            <LoginPage />
          </Route>
        </IonRouterOutlet>
      </IonReactRouter>
    );
  }

  // Authenticated - show main app
  return (
    <IonReactRouter>
      <AuthenticatedApp />
    </IonReactRouter>
  );
};

// ============================================
// APP WRAPPER
// ============================================

const App: React.FC = () => (
  <IonApp>
    <AuthProvider>
      <AppRoot />
    </AuthProvider>
  </IonApp>
);

export default App;
